import JSZip from 'jszip';
import { photosApi } from './api';

/**
 * Photo helpers for the Photo Manager tool.
 *
 * HEIC/HEIF is decoded on the SERVER (see photosApi.convertHeicToJpeg) because
 * in-browser HEIC decoders are unreliable across browsers/environments. Every
 * other image type is normalized to JPEG in the browser through a <canvas>.
 * Rotation is baked into the bytes at export time (download/upload) rather than
 * on every click, so repeated rotates never compound JPEG artifacts.
 */

/** Output JPEG quality for both conversion and rotation re-encodes. */
export const JPEG_QUALITY = 0.9;

const HEIC_EXTENSIONS = ['.heic', '.heif'];
const HEIC_MIME = ['image/heic', 'image/heif', 'image/heic-sequence', 'image/heif-sequence'];

/**
 * Extensions we treat as "a picture" when sifting a folder or zip. HEIC/HEIF are
 * included because the whole point of the tool is to convert them; the rest are
 * formats a browser <canvas> can decode directly.
 */
const PICTURE_EXTENSIONS = [
  ...HEIC_EXTENSIONS,
  '.jpg',
  '.jpeg',
  '.png',
  '.webp',
  '.gif',
  '.bmp',
  '.tif',
  '.tiff',
  '.avif',
];

function extension(name: string): string {
  const dot = name.lastIndexOf('.');
  return dot === -1 ? '' : name.slice(dot).toLowerCase();
}

/** True when a filename/MIME pair looks like an image we can import. */
export function isPicture(name: string, mimeType = ''): boolean {
  if (mimeType.toLowerCase().startsWith('image/')) return true;
  return PICTURE_EXTENSIONS.includes(extension(name));
}

/** True for Apple HEIC/HEIF, which needs the WASM decoder (not canvas). */
export function isHeic(file: { name: string; type: string }): boolean {
  return HEIC_MIME.includes(file.type.toLowerCase()) || HEIC_EXTENSIONS.includes(extension(file.name));
}

/** HEIF `ftyp` brands that mean "this is really a HEIC/HEIF image". */
const HEIF_BRANDS = new Set([
  'heic', 'heix', 'heif', 'hevc', 'hevx', 'mif1', 'msf1', 'heim', 'heis', 'hevm', 'hevs',
]);

/**
 * Sniff the real image format from the file's magic bytes — the extension lies.
 * Phones and upload pipelines routinely hand us a JPEG named `.HEIC`, so trusting
 * the name sends a perfectly decodable JPEG to the HEIC decoder, which then
 * rejects it. Only bytes that are actually HEIF go through the server decoder;
 * everything else the browser can decode itself.
 */
export async function sniffImageKind(file: File): Promise<'heic' | 'browser'> {
  const buf = new Uint8Array(await file.slice(0, 16).arrayBuffer());
  // ISO-BMFF `ftyp` box at offset 4, brand at offset 8.
  const isFtyp = buf[4] === 0x66 && buf[5] === 0x74 && buf[6] === 0x79 && buf[7] === 0x70;
  if (isFtyp) {
    const brand = String.fromCharCode(buf[8], buf[9], buf[10], buf[11]).toLowerCase();
    if (HEIF_BRANDS.has(brand)) return 'heic';
  }
  // JPEG (FF D8 FF), PNG, GIF, BMP, WebP — all decodable by <canvas> directly.
  return 'browser';
}

/** Swap (or append) a `.jpg` extension on a filename. */
export function toJpegName(name: string): string {
  const slash = Math.max(name.lastIndexOf('/'), name.lastIndexOf('\\'));
  const base = slash === -1 ? name : name.slice(slash + 1);
  const dot = base.lastIndexOf('.');
  const stem = dot === -1 ? base : base.slice(0, dot);
  return `${stem.trim() || 'photo'}.jpg`;
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('The image could not be decoded.'));
    img.src = src;
  });
}

/**
 * Draw `source` onto a canvas rotated by `rotation` degrees (clockwise) and
 * export it as a JPEG Blob. rotation must be one of 0/90/180/270.
 */
async function encodeToJpeg(source: Blob, rotation: number): Promise<Blob> {
  const url = URL.createObjectURL(source);
  try {
    const img = await loadImage(url);
    const swap = rotation === 90 || rotation === 270;
    const canvas = document.createElement('canvas');
    canvas.width = swap ? img.naturalHeight : img.naturalWidth;
    canvas.height = swap ? img.naturalWidth : img.naturalHeight;
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Your browser does not support canvas image export.');
    ctx.translate(canvas.width / 2, canvas.height / 2);
    ctx.rotate((rotation * Math.PI) / 180);
    ctx.drawImage(img, -img.naturalWidth / 2, -img.naturalHeight / 2);
    return await new Promise<Blob>((resolve, reject) =>
      canvas.toBlob(
        (b) => (b ? resolve(b) : reject(new Error('The image could not be encoded.'))),
        'image/jpeg',
        JPEG_QUALITY,
      ),
    );
  } finally {
    URL.revokeObjectURL(url);
  }
}

/**
 * Convert any supported image to a JPEG File (rotation 0). Routing is by CONTENT,
 * not extension: genuine HEIF bytes are decoded server-side; anything the browser
 * can decode (JPEG/PNG/WebP/… — including a JPEG mislabeled `.HEIC`) is re-encoded
 * via canvas. A multi-image HEIC (e.g. a Live Photo) yields its primary frame.
 */
export async function convertToJpeg(file: File): Promise<File> {
  const name = toJpegName(file.name);
  if ((await sniffImageKind(file)) === 'heic') {
    const blob = await photosApi.convertHeicToJpeg(file);
    return new File([blob], name, { type: 'image/jpeg' });
  }
  const blob = await encodeToJpeg(file, 0);
  return new File([blob], name, { type: 'image/jpeg' });
}

/**
 * Return `jpeg` rotated by `rotation` degrees, baked into fresh bytes. Rotation 0
 * returns the input untouched (no needless re-encode).
 */
export async function bakeRotation(jpeg: File, rotation: number): Promise<File> {
  const normalized = ((rotation % 360) + 360) % 360;
  if (normalized === 0) return jpeg;
  const blob = await encodeToJpeg(jpeg, normalized);
  return new File([blob], jpeg.name, { type: 'image/jpeg' });
}

/** Bundle the given JPEG files into a single ZIP Blob, de-duplicating names. */
export async function zipFiles(files: File[]): Promise<Blob> {
  const zip = new JSZip();
  const used = new Map<string, number>();
  for (const file of files) {
    let name = file.name;
    const seen = used.get(name.toLowerCase());
    if (seen != null) {
      const dot = name.lastIndexOf('.');
      const stem = dot === -1 ? name : name.slice(0, dot);
      const ext = dot === -1 ? '' : name.slice(dot);
      name = `${stem} (${seen + 1})${ext}`;
      used.set(file.name.toLowerCase(), seen + 1);
    } else {
      used.set(name.toLowerCase(), 1);
    }
    zip.file(name, file);
  }
  return zip.generateAsync({ type: 'blob' });
}

/**
 * Expand a dropped/selected `.zip`, returning only the picture entries as Files.
 * Directory entries, macOS resource forks (`__MACOSX`, dot-underscore), and
 * non-image files are ignored.
 */
export async function extractPicturesFromZip(zipFile: File): Promise<File[]> {
  const zip = await JSZip.loadAsync(zipFile);
  const out: File[] = [];
  const entries = Object.values(zip.files);
  for (const entry of entries) {
    if (entry.dir) continue;
    const base = entry.name.split('/').pop() ?? entry.name;
    if (!base || base.startsWith('.') || entry.name.startsWith('__MACOSX/')) continue;
    if (!isPicture(base)) continue;
    const blob = await entry.async('blob');
    out.push(new File([blob], base, { type: blob.type || 'application/octet-stream' }));
  }
  return out;
}

/**
 * Pull image Files out of a drag-and-drop, expanding any dropped folders
 * (Chromium/WebKit `webkitGetAsEntry`) and `.zip` archives. Non-image files are
 * silently skipped. Falls back to `DataTransfer.files` when the entries API is
 * unavailable.
 */
export async function collectDroppedPictures(dt: DataTransfer): Promise<File[]> {
  const entries: FileSystemEntry[] = [];
  const items = Array.from(dt.items ?? []);
  for (const item of items) {
    if (item.kind !== 'file') continue;
    const entry = item.webkitGetAsEntry?.();
    if (entry) entries.push(entry);
  }

  const out: File[] = [];
  if (entries.length > 0) {
    const walked = await Promise.all(entries.map(walkEntry));
    out.push(...walked.flat());
  } else {
    // No entries API — fall back to the flat file list.
    out.push(...Array.from(dt.files));
  }

  return expandArchives(out);
}

/** Filter/expand a flat File[] (e.g. from an <input>): keep pictures, unzip zips. */
export async function collectPictureFiles(files: File[]): Promise<File[]> {
  return expandArchives(files);
}

async function expandArchives(files: File[]): Promise<File[]> {
  const out: File[] = [];
  for (const file of files) {
    if (extension(file.name) === '.zip' || file.type === 'application/zip') {
      try {
        out.push(...(await extractPicturesFromZip(file)));
      } catch {
        // A corrupt/unsupported archive is skipped rather than aborting the drop.
      }
      continue;
    }
    if (isPicture(file.name, file.type)) out.push(file);
  }
  return out;
}

/** Recurse a dropped directory entry, collecting file entries as Files. */
function walkEntry(entry: FileSystemEntry): Promise<File[]> {
  if (entry.isFile) {
    return new Promise((resolve) => {
      (entry as FileSystemFileEntry).file(
        (file) => resolve([file]),
        () => resolve([]),
      );
    });
  }
  if (entry.isDirectory) {
    const reader = (entry as FileSystemDirectoryEntry).createReader();
    return new Promise((resolve) => {
      const all: FileSystemEntry[] = [];
      const readBatch = () => {
        reader.readEntries(
          (batch) => {
            if (batch.length === 0) {
              Promise.all(all.map(walkEntry)).then((r) => resolve(r.flat()));
              return;
            }
            all.push(...batch);
            readBatch(); // readEntries returns at most 100 at a time — keep going.
          },
          () => resolve([]),
        );
      };
      readBatch();
    });
  }
  return Promise.resolve([]);
}

/** Trigger a browser "Save As" for a Blob. */
export function saveBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}
